import { useParams, useNavigate } from 'react-router-dom';
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  useColorModeValue,
  Heading,
  Button,
  HStack,
  Flex,
  VStack,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { apiUpdateBinderChecklist } from 'api';
import type {
  APIHomeBinderData,
  APIPersonalHomeApplicationForm,
} from 'types/api.types';
import {
  HomeReportHeader,
  HomeUploadSummaryCard,
  HomeLocationsCard,
  NonStandardLenderCard,
  MarketApprovalsCard,
} from './components';
import {
  ValidationCards,
  RemarksCard,
  StickyNavigation,
} from '../binder_report/components';
import { processRemarksData } from '../binder_report/processors';
import { processHomeUploadSummaryData } from './processors';

const NAV_SECTIONS = [
  { id: 'validations', label: 'Validations' },
  { id: 'non-standard-lender', label: 'Non-Standard Lender' },
  { id: 'market-approvals', label: 'Market Approvals' },
  { id: 'locations', label: 'Locations' },
  { id: 'upload-summary', label: 'Upload Summary' },
  { id: 'remarks', label: 'Submission Remarks' },
];

const HomeBinderReport = ({ data }: { data: APIHomeBinderData | null }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState<string>('');

  const bgColor = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const handleNavigateToOverview = useCallback(() => {
    navigate(`/admin/policies/overview/${uuid}`);
  }, [navigate, uuid]);

  const handleChecklistUpdate = useCallback(
    async (checklistData: {
      [validationId: string]: { checked: boolean };
    }): Promise<boolean> => {
      if (!uuid) return false;

      try {
        await apiUpdateBinderChecklist(uuid, checklistData);
        queryClient.invalidateQueries({ queryKey: ['binder-report', uuid] });
        return true;
      } catch (error) {
        console.error('Error updating binder checklist:', error);
        return false;
      }
    },
    [uuid, queryClient],
  );

  // Process remarks data
  const remarksData = useMemo(
    () => processRemarksData(data?.remarks_data || []),
    [data?.remarks_data],
  );

  // Process upload summary data
  const uploadSummaryData = useMemo(() => {
    if (!data) return null;
    return processHomeUploadSummaryData(data);
  }, [data]);

  const locationsData = uploadSummaryData?.data?.locations;
  const billingAndSummaryData = uploadSummaryData?.data;

  return (
    <Card bg={bgColor} p={8}>
      {/* Header with navigation */}
      <Box borderColor={borderColor}>
        <Flex justify="space-between" align="center" mb={6}>
          <Box>
            <Heading size="md" color={textColor}>
              Home Binder Report
            </Heading>
          </Box>
          <HStack spacing={3}>
            <Button
              variant="outline"
              colorScheme="teal"
              onClick={handleNavigateToOverview}
              size="sm"
            >
              Binder Overview
            </Button>
          </HStack>
        </Flex>

        {/* Report Header Information */}
        <HomeReportHeader
          applicationData={
            data?.application_data as APIPersonalHomeApplicationForm
          }
        />
      </Box>

      {/* Sticky Navigation */}
      <StickyNavigation
        sections={NAV_SECTIONS}
        activeSection={activeSection}
        onActiveChange={setActiveSection}
      />

      {/* Report Sections */}
      <Box mt={6}>
        <VStack spacing={4} align="stretch">
          {/* Validation Cards Section */}
          <Box id="validations">
            <ValidationCards
              validations={data?.validation_data?.validations || []}
              checklistData={data?.checklist_data?.validations || {}}
              onChecklistUpdate={handleChecklistUpdate}
            />
          </Box>

          {/* Non-Standard Lender Section */}
          <Box id="non-standard-lender">
            <NonStandardLenderCard
              applicationData={
                data?.application_data as APIPersonalHomeApplicationForm
              }
              remarks={data?.remarks_data}
            />
          </Box>

          {/* Market Approvals Section */}
          <Box id="market-approvals">
            <MarketApprovalsCard
              validations={data?.validation_data?.validations}
              remarks={data?.remarks_data}
            />
          </Box>

          {/* Locations Section */}
          {locationsData && (
            <Box id="locations">
              <HomeLocationsCard sectionData={locationsData} />
            </Box>
          )}

          {/* Upload Summary Section */}
          {billingAndSummaryData && (
            <Box id="upload-summary">
              <HomeUploadSummaryCard sectionData={billingAndSummaryData} />
            </Box>
          )}

          {/* Remarks Section */}
          <Box id="remarks">
            <Heading size="sm" mb={4} color={textColor}>
              Submission Remarks
            </Heading>
            <RemarksCard sectionData={remarksData} />
          </Box>
        </VStack>
      </Box>
    </Card>
  );
};

export default HomeBinderReport;
